import React, { useEffect, useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { rptService } from '../../../../services/rptService';
import {
  Box,
  Container,
  Typography,
  Grid,
  Card,
  CardContent,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  InputAdornment,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  IconButton,
  Tooltip,
  CircularProgress,
  Breadcrumbs,
  Link
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  Search as SearchIcon,
  Refresh as RefreshIcon,
  Assessment as AssessmentIcon,
  FilterList as FilterListIcon,
  Home as HomeIcon,
  Download as DownloadIcon
} from '@mui/icons-material';

const AssessmentsPage = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [properties, setProperties] = useState([]);
  const [assessments, setAssessments] = useState([]);
  const [barangay, setBarangay] = useState('');
  const [year, setYear] = useState('');
  const [search, setSearch] = useState('');

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const [propsRes, assessRes] = await Promise.all([
        rptService.getAllProperties(),
        rptService.getAllPropertyAssessments()
      ]);
      if (propsRes.success) setProperties(propsRes.data);
      if (assessRes.success) setAssessments(assessRes.data);
    } finally {
      setLoading(false);
    }
  };

  const propMap = useMemo(() => {
    return (properties || []).reduce((acc, p) => { acc[p.property_id] = p; return acc; }, {});
  }, [properties]);

  const barangays = useMemo(() => {
    return Array.from(new Set((properties || []).map(p => p.barangay).filter(Boolean))).sort();
  }, [properties]);

  const filtered = useMemo(() => {
    return (assessments || []).filter(a => {
      const p = propMap[a.property_id];
      if (!p) return false;
      if (barangay && p.barangay !== barangay) return false;
      if (year && String(a.effectivity_year) !== String(year)) return false;
      const s = search.trim().toLowerCase();
      if (s) {
        const td = String(p.td_number || '').toLowerCase();
        const owner = String(p.owner_name || '').toLowerCase();
        return td.includes(s) || owner.includes(s);
      }
      return true;
    }).sort((a, b) => b.effectivity_year - a.effectivity_year);
  }, [assessments, propMap, barangay, year, search]);

  const toCurrency = (n) => `₱${parseFloat(n || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

  const handleExport = () => {
    if (filtered.length === 0) {
      alert("No data to export");
      return;
    }

    const csvContent = [
      ['TD Number', 'Owner', 'Barangay', 'Effectivity Year', 'Assessed Value', 'Revision Reason', 'Remarks'],
      ...filtered.map(a => {
        const p = propMap[a.property_id] || {};
        return [
          `"${(p.td_number || '').replace(/"/g, '""')}"`,
          `"${(p.owner_name || '').replace(/"/g, '""')}"`,
          `"${(p.barangay || '').replace(/"/g, '""')}"`,
          a.effectivity_year,
          a.assessed_value,
          `"${(a.revision_reason || '').replace(/"/g, '""')}"`,
          `"${(a.remarks || '').replace(/"/g, '""')}"`
        ];
      })
    ].map(e => e.join(',')).join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    if (link.download !== undefined) {
      const url = URL.createObjectURL(blob);
      link.setAttribute('href', url);
      link.setAttribute('download', `assessments_${new Date().toISOString().split('T')[0]}.csv`);
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  };

  return (
    <Box sx={{ bgcolor: '#f8fafc', minHeight: '100vh', pb: 4 }}>
      <Container maxWidth="xl" sx={{ pt: 4 }}>
        <Box sx={{ mb: 4, display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
          <Box>
            <Breadcrumbs aria-label="breadcrumb" sx={{ mb: 2, fontSize: '0.875rem' }}>
              <Link 
                underline="hover" 
                color="inherit" 
                sx={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}
                onClick={() => navigate('/portal/dashboard')}
              >
                <HomeIcon sx={{ mr: 0.5 }} fontSize="inherit" />
                Portal
              </Link>
              <Link 
                underline="hover" 
                color="inherit" 
                sx={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}
                onClick={() => navigate('/portal/property-tax/dashboard')}
              >
                RPT System
              </Link>
              <Typography color="text.primary" sx={{ fontWeight: 500 }}>Assessments</Typography>
            </Breadcrumbs>
            
            <Typography variant="h4" sx={{ 
              fontWeight: 800, 
              color: '#1e293b', 
              letterSpacing: '-0.02em',
              background: 'linear-gradient(45deg, #1e293b 30%, #334155 90%)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              display: 'flex',
              alignItems: 'center',
              gap: 2,
              mb: 1
            }}>
              <AssessmentIcon fontSize="large" sx={{ color: '#3b82f6' }} /> Tax Assessments
            </Typography>
            <Typography variant="body1" color="text.secondary" sx={{ maxWidth: '800px' }}>
              Overview of property assessment history and revisions
            </Typography>
          </Box>
          
          <Box sx={{ display: 'flex', gap: 2 }}>
            <Button
              variant="outlined"
              startIcon={<DownloadIcon />}
              onClick={handleExport}
              disabled={loading || filtered.length === 0}
              sx={{ height: 48, borderColor: '#e2e8f0', color: '#475569', '&:hover': { borderColor: '#cbd5e1', bgcolor: '#f8fafc' } }}
            >
              Export CSV
            </Button>
            <Button 
              variant="contained" 
              startIcon={loading ? <CircularProgress size={20} color="inherit" /> : <RefreshIcon />} 
              onClick={loadData}
              disabled={loading}
              sx={{ 
                height: 48, 
                bgcolor: '#3b82f6', 
                '&:hover': { bgcolor: '#2563eb' },
                boxShadow: '0 4px 6px -1px rgb(59 130 246 / 0.5)'
              }}
            >
              Refresh
            </Button>
          </Box>
        </Box>

        <Card variant="outlined" sx={{ boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.05)' }}>
          <CardContent sx={{ p: 3 }}>

            <Grid container spacing={2} sx={{ mb: 3 }}>
              <Grid item xs={12} md={3}>
                <FormControl fullWidth size="small">
                  <InputLabel>Barangay</InputLabel>
                  <Select
                    value={barangay}
                    label="Barangay"
                    onChange={(e) => setBarangay(e.target.value)}
                  >
                    <MenuItem value="">All</MenuItem>
                    {barangays.map(b => (<MenuItem key={b} value={b}>{b}</MenuItem>))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={3}>
                <TextField
                  label="Effectivity Year"
                  type="number"
                  value={year}
                  onChange={(e) => setYear(e.target.value)}
                  fullWidth
                  size="small"
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  label="Search TD or Owner"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  fullWidth
                  size="small"
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon color="action" />
                      </InputAdornment>
                    ),
                  }}
                  placeholder="e.g., TD number or owner"
                />
              </Grid>
            </Grid>

            {loading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                <CircularProgress />
                <Typography sx={{ ml: 2 }}>Loading assessments...</Typography>
              </Box>
            ) : (
              <TableContainer component={Paper} variant="outlined">
                <Table size="small">
                  <TableHead sx={{ bgcolor: 'grey.100' }}>
                    <TableRow>
                      <TableCell>TD Number</TableCell>
                      <TableCell>Owner</TableCell>
                      <TableCell>Barangay</TableCell>
                      <TableCell align="center">Effectivity Year</TableCell>
                      <TableCell align="right">Assessed Value</TableCell>
                      <TableCell>Revision Reason</TableCell>
                      <TableCell>Remarks</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {filtered.map(a => {
                      const p = propMap[a.property_id] || {};
                      return (
                        <TableRow 
                          key={a.assessment_id} 
                          hover
                          sx={{ 
                            '&:nth-of-type(odd)': { bgcolor: '#ffffff' },
                            '&:nth-of-type(even)': { bgcolor: '#f8fafc' },
                            '&:hover': { bgcolor: '#f1f5f9 !important' },
                            transition: 'background-color 0.1s'
                          }}
                        >
                          <TableCell>
                            <Typography variant="body2" fontWeight="bold">
                              {p.td_number || 'N/A'}
                            </Typography>
                          </TableCell>
                          <TableCell>{p.owner_name || 'N/A'}</TableCell>
                          <TableCell>{p.barangay || '—'}</TableCell>
                          <TableCell align="center">
                            <Chip label={a.effectivity_year} size="small" variant="outlined" />
                          </TableCell>
                          <TableCell align="right">
                            <Typography variant="body2" fontWeight="bold" color="success.main">
                              {toCurrency(a.assessed_value)}
                            </Typography>
                          </TableCell>
                          <TableCell sx={{ color: 'text.secondary', fontSize: '0.875rem' }}>
                            {a.revision_reason || '—'}
                          </TableCell>
                          <TableCell sx={{ color: 'text.secondary', fontSize: '0.875rem' }}>
                            {a.remarks || '—'}
                          </TableCell>
                        </TableRow>
                      );
                    })}
                    {filtered.length === 0 && (
                      <TableRow>
                        <TableCell colSpan={7} align="center" sx={{ py: 3, color: 'text.secondary' }}>
                          No assessments found
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </CardContent>
        </Card>

        {/* Footer */}
        <Box component="footer" sx={{ py: 6, textAlign: 'center', borderTop: '1px solid #e2e8f0', mt: 8 }}>
          <Typography variant="body2" color="text.secondary">
            © {new Date().getFullYear()} Real Property Tax System • Official Government Portal
          </Typography>
          <Typography variant="caption" color="text.disabled" sx={{ mt: 1, display: 'block' }}>
            Authorized Personnel Only • Confidential Information
          </Typography>
        </Box>
      </Container>
    </Box>
  );
};

export default AssessmentsPage;
